<script setup>
import { debounce } from "lodash-es";
import { reactive, ref } from "vue";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import VocabService from "@/common/api/vocab.service";
import { isDownArrow, isEnter, isEscape, isUpArrow } from "@/common/event-helpers";
import { SEARCH_DEBOUNCE_TIME } from "@/common/search";
import IconEnter from "@/components/icons/IconEnter.vue";
import IconPlus from "@/components/icons/IconPlus.vue";
import IconWhiteCross from "@/components/icons/white/IconWhiteCross.vue";
import IconWhiteSearch from "@/components/icons/white/IconWhiteSearch.vue";
import { i18n } from "@/i18n";

const props = defineProps({
  language: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["addWord", "setBackdrop"]);

const { t } = i18n.global;

const input = ref(null);

const resultWrapper = ref(null);

const resultRefs = ref([]);

const state = reactive({
  showSearch: true,
  query: "",
  results: [],
  hasFocus: false,
  activeIndex: 0
});

const getResults = debounce(async () => {
  const query = state.query.trim();
  if (query === "") {
    state.results = [];
    emit("setBackdrop", false);
    return;
  }

  state.results = (await VocabService.search(query, props.language)).data.data;
  emit("setBackdrop", state.results.length > 0);
}, SEARCH_DEBOUNCE_TIME);

const handleActiveItemVisibilityInViewport = () => {
  const item = resultRefs.value[state.activeIndex];
  const wrapper = resultWrapper.value;
  if (!item || !wrapper) {
    return;
  }

  if (
    item.offsetTop < wrapper.scrollTop ||
    item.offsetTop > wrapper.scrollTop + wrapper.offsetHeight
  ) {
    wrapper.scrollTo({
      top: item.offsetTop,
      behavior: "smooth"
    });
  }
};
const handleKeydownEvent = (event) => {
  // Ensure focus whenever a key is pressed, unless we un-focus.
  if (!isEscape(event) && !isEnter(event)) {
    state.hasFocus = true;
  }

  switch (true) {
    case isUpArrow(event):
      event.preventDefault();
      state.activeIndex--;

      // Loop when we can't go further up the list
      if (state.activeIndex < 0) {
        state.activeIndex = state.results.length - 1;
      }

      handleActiveItemVisibilityInViewport();

      break;

    case isDownArrow(event):
      event.preventDefault();

      state.activeIndex++;

      // Loop when we can't go further down the list
      if (state.activeIndex >= state.results.length) {
        state.activeIndex = 0;
      }

      handleActiveItemVisibilityInViewport();

      break;

    case isEscape(event):
      if (state.hasFocus) {
        event.preventDefault();
        setFocus(false);

        break;
      }

      state.query = "";

      break;

    case isEnter(event):
      if (state.hasFocus) {
        emit("addWord", state.results[state.activeIndex]);
        setFocus(false);

        break;
      }

      setFocus(true);

      break;
  }
};

const setFocus = (value) => {
  // This setTimeout prevents an edge case where the results
  // are blurred before we get to emit addWord, which results
  // in the word not getting added since the word completion
  // window closes so fast that you can't click it with your
  // mouse.
  setTimeout(() => {
    state.hasFocus = value;
    emit("setBackdrop", value && state.results.length > 0);

    if (!value) {
      state.activeIndex = 0;
    }
  }, 5);
};
</script>

<template>
  <div class="word-list-translations-search-bar">
    <div
      class="word-list-translations-search-bar__search-input-wrapper"
      :class="{ 'word-list-translations-search-bar__search-input-wrapper--focus': state.hasFocus }"
    >
      <input
        ref="input"
        v-model="state.query"
        class="word-list-translations-search-bar__search-input"
        type="search"
        name="search"
        autocomplete="off"
        autocapitalize="off"
        :placeholder="t('vocab.vocabList.searchBar.placeholder')"
        @keydown="handleKeydownEvent($event)"
        @input="getResults()"
        @focus="setFocus(true)"
        @blur="setFocus(false)"
      />
      <div class="word-list-translations-search-bar__icon-wrapper">
        <div>
          <button
            v-if="state.query.length > 0"
            class="word-list-translations-search-bar__icon-button"
            :aria-label="t('vocab.vocabList.searchBar.reset')"
            @click="state.query = ''"
          >
            <IconWhiteCross class="word-list-translations-search-bar__reset-icon" />
          </button>
        </div>
        <div class="word-list-translations-search-bar__separator py-1"></div>
        <div>
          <button class="word-list-translations-search-bar__icon-button" @click="input?.focus()">
            <IconWhiteSearch class="word-list-translations-search-bar__search-icon" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="state.results.length > 0 && state.hasFocus"
      ref="resultWrapper"
      class="word-list-translations-search-bar__result-wrapper"
      @mouseleave="state.activeIndex = -1"
    >
      <div
        v-for="(result, index) in state.results"
        :key="index"
        :ref="(el) => (resultRefs[index] = el)"
      >
        <button
          class="word-list-translations-search-bar__add-word"
          :class="{
            'word-list-translations-search-bar__add-word--selected': state.activeIndex === index
          }"
          :aria-label="t('vocab.vocabList.searchBar.addToList', { word: result.translation })"
          @mousedown="emit('addWord', result)"
          @mouseenter="state.activeIndex = index"
        >
          <TgIconButton size="sm" variant="solid" presentational>
            <IconPlus role="presentation" />
          </TgIconButton>
          <span>
            <span class="word-list-translations-search-bar__translation">{{
              result.translation
            }}</span>
            &nbsp;
            <span class="word-list-translations-search-bar__word-class">
              {{ result.translationWordclass.short }}
            </span>
          </span>
          <span class="word-list-translations-search-bar__hint">
            {{ t("vocab.vocabList.searchBar.selectHint") }}
            <IconEnter class="word-list-translations-search-bar__hint-icon" />
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.word-list-translations-search-bar {
  &__search-input-wrapper {
    display: grid;
    grid-template-columns: 1fr 88px;
    padding-left: 8px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 4px;

    &--focus {
      border: 1px solid rgb(@theme-colors[@secondary][base]);
      box-shadow: 0px 0px 0px 4px rgba(@theme-colors[@secondary][base], 0.3);
    }
  }

  &__search-input {
    width: 100%;
    padding: 0;
    border: none;
    outline: none;
    background-color: rgb(@colors[@white]);
    font-size: 1rem;

    &::placeholder {
      color: rgb(@theme-colors[@grey][50]);
    }

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  }

  &__icon-wrapper {
    display: grid;
    grid-template-columns: 1fr 1px 1fr;
    grid-template-rows: 46px;
    border-radius: 4px;
    background: rgb(@colors[@white]);
  }

  &__icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 11px;
    border: none;
    background: none;
    -webkit-appearance: none;
    appearance: none;
  }

  &__separator {
    align-self: center;
    height: 32px;
    border-left: 1px solid rgb(@theme-colors[@grey][50]);
  }

  &__search-icon {
    color: rgb(@theme-colors[@primary][base]);
  }

  &__reset-icon {
    color: rgb(@theme-colors[@grey][50]);
  }

  &__result-wrapper {
    position: relative;
    z-index: 1;
    width: 100%;
    max-height: 350px;
    margin-top: 10px;
    padding: 8px;
    overflow-y: scroll;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 4px;
    background-color: rgb(@colors[@white]);
    box-shadow: 6px 6px 40px rgba(0, 0, 0, 0.12);
  }

  &__add-word {
    display: grid;
    grid-template-columns: auto 1fr;
    width: 100%;
    margin: 0 0 4px 0;
    padding: 8px;
    border: none;
    border-radius: 4px;
    background: none;
    text-align: left;
    gap: 8px;

    #media-breakpoint-up(md, {
      grid-template-columns: auto 1fr minmax(57px, auto);
    });

    &:hover,
    &--selected {
      background-color: rgb(@theme-colors[@grey][10]);

      & > .word-list-translations-search-bar__hint {
        #media-breakpoint-up(md, {
           display: block;
        });
      }
    }
  }

  &__translation {
    color: rgb(@theme-colors[@primary][base]);
  }

  &__word-class {
    color: rgb(@theme-colors[@secondary][base]);
  }

  &__hint {
    display: none;
    color: rgb(@theme-colors[@grey][50]);
  }

  &__hint-icon {
    width: 16px;
    height: 16px;
    padding: 3px 3px 2px 3px;
    border-radius: 4px;
    background-color: rgb(@theme-colors[@grey][50]);
  }
}
</style>
